import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import PhotoUploader from '../../components/PhotoUploader';
import InfoTip from '../../components/InfoTip';
import HostSmsAuthorization from '../../components/compliance/HostSmsAuthorization';
import { coupleNames as fmtCouple } from '../../utils/coupleNames';
import {
  Mail, MessageSquare, Send, Wand2, Loader2, Check, X, ShieldAlert, ShieldCheck,
  Users, Sparkles, ExternalLink, Eye, AlertTriangle, Lock, Unlock, RotateCcw, MailPlus,
  Search, Maximize2, Minimize2, Save, Trash2, Bookmark, Filter,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { buildDeliveryTooltip, fmtDeliveryTime } from './Guests';

const TONES = [
  { id: 'warm', label: 'Warm & personal' },
  { id: 'playful', label: 'Playful' },
  { id: 'formal', label: 'Formal' },
  { id: 'poetic', label: 'Poetic' },
];

const MERGE_HINTS = [
  '{firstName}', '{guestAndPartner}', '{coupleNames}', '{weddingDate}', '{weddingTime}',
  '{venue}', '{venueAddress}', '{replyByDate}',
  '{rsvpLink}', '{rsvpShortLink}', '{siteUrl}', '{couplePhotoBlock}',
];

function renderPreview(template, ctx) {
  if (!template) return '';
  return template.replace(/\{(\w+)\}/g, (_, k) => (ctx[k] ?? ''));
}

export default function Invitations() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [tmpl, setTmpl] = useState(null);
  const [guests, setGuests] = useState([]);
  const [profile, setProfile] = useState({});
  const [siteSlug, setSiteSlug] = useState('');
  const [couplePhoto, setCouplePhoto] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [tone, setTone] = useState('warm');
  // Toggle between plain-English prose editor (friendly, default) and the
  // raw HTML editor. Non-technical couples never need to see the HTML view.
  const [emailMode, setEmailMode] = useState('plain');
  const [details, setDetails] = useState({
    startTime: '', foodStyle: '', entertainment: '', attire: '', mood: '', otherNotes: '',
  });
  const [selection, setSelection] = useState({}); // { guestId: 'email' | 'sms' | 'both' | 'skip' }
  // Recipient picker filters — shared by the inline sidebar view AND the
  // full-screen manager modal so selections/filters stay in lock-step.
  const [pkQuery, setPkQuery] = useState('');
  const [pkSide, setPkSide] = useState('all');       // all | Bride | Groom | Both
  const [pkGroup, setPkGroup] = useState('all');     // all | Family | Friends | Work | Other
  const [pkHasEmail, setPkHasEmail] = useState(false);
  const [pkHasPhone, setPkHasPhone] = useState(false);
  const [pkStatus, setPkStatus] = useState('all');   // all | unsent | delivered | opened | bounced
  const [pkRsvp, setPkRsvp] = useState('all');       // all | pending | yes | no | unreplied (pending+no-reply)
  const [showRecipientMgr, setShowRecipientMgr] = useState(false);
  // Saved segments live in localStorage per-user so cross-device sync isn't
  // required for MVP. Format: [{ id, name, filters:{...} }]
  const segmentsKey = user?.email ? `wf.recipient_segments.${user.email}` : 'wf.recipient_segments.anon';
  const [savedSegments, setSavedSegments] = useState(() => {
    try {
      const raw = localStorage.getItem(segmentsKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(segmentsKey, JSON.stringify(savedSegments)); } catch {}
  }, [savedSegments, segmentsKey]);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testChannel, setTestChannel] = useState('email');
  const [testBusy, setTestBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, t, g, p, w] = await Promise.all([
          client.get('/invitations/status'),
          client.get('/invitations/template'),
          client.get('/guests'),
          client.get('/profile').catch(() => ({ data: {} })),
          client.get('/website/config').catch(() => ({ data: null })),
        ]);
        setStatus(s.data);
        setTmpl(t.data);
        setGuests(g.data);
        setProfile(p.data);
        setSiteSlug(w.data?.slug || '');
        setCouplePhoto(w.data?.couplePhoto || '');
        setTestEmail(user?.email || '');
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [user?.email]);

  const previewCtx = useMemo(() => {
    const origin = window.location.origin;
    const first = guests[0];
    const firstName = (first?.name || profile?.partner1 || 'Alex').split(' ')[0];
    // Match backend _format_wedding_date
    let dateStr = 'our wedding day';
    if (profile?.weddingDate) {
      const d = new Date(profile.weddingDate + 'T00:00:00');
      if (!isNaN(d)) {
        dateStr = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      } else {
        dateStr = profile.weddingDate;
      }
    }
    // Reply-by date — mirrors backend build_context so the preview stays
    // in lock-step with what actually gets sent. Empty replyByDate falls
    // back to the soft "at your earliest convenience" line.
    let replyByFormatted = '';
    if (profile?.replyByDate) {
      const d = new Date(profile.replyByDate + 'T00:00:00');
      if (!isNaN(d)) {
        replyByFormatted = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      } else {
        replyByFormatted = profile.replyByDate;
      }
    }
    const replyByLine = replyByFormatted
      ? `Kindly reply by ${replyByFormatted}`
      : 'Kindly reply at your earliest convenience';
    return {
      firstName,
      coupleNames: fmtCouple(profile, 'Us'),
      weddingDate: dateStr,
      weddingTime: details.startTime || profile?.weddingTime || 'TBD',
      venue: profile?.venue || profile?.location || 'the venue',
      venueAddress: profile?.venueAddress || '',
      location: profile?.location || '',
      rsvpLink: `${origin}/rsvp/preview-token`,
      siteUrl: siteSlug ? `${origin}/wedding/${siteSlug}` : '',
      couplePhoto: couplePhoto || '',
      couplePhotoBlock: couplePhoto
        ? `<div style="text-align:center;margin:0 0 20px"><img src="${couplePhoto}" alt="" style="max-width:100%;width:320px;height:auto;border-radius:16px;display:inline-block" /></div>`
        : '',
      replyByDate: replyByFormatted,
      replyByLine,
    };
  }, [guests, profile, siteSlug, details.startTime, couplePhoto]);

  const saveCouplePhoto = async (url) => {
    setCouplePhoto(url);
    try {
      await client.put('/website/config', { couplePhoto: url });
    } catch (e) {
      toast({ title: "Couldn't save photo", description: e?.response?.data?.detail || e.message });
    }
  };

  const saveTemplate = async (patch) => {
    if (!tmpl) return;
    const next = { ...tmpl, ...patch };
    setTmpl(next);
    setSaving(true);
    try {
      const { data } = await client.put('/invitations/template', patch);
      setTmpl(data);
    } catch (e) {
      toast({ title: "Couldn't save template", description: e?.response?.data?.detail || e.message });
    }
    setSaving(false);
  };

  const generateAI = async () => {
    if (tmpl?.emailLocked && tmpl?.smsLocked) {
      toast({ title: 'Both are locked', description: 'Unlock the email or SMS to regenerate.' });
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await client.post('/invitations/generate', { tone, ...details });
      setTmpl(data);
      const bits = [
        !data.emailLocked && 'email',
        !data.smsLocked && 'SMS',
      ].filter(Boolean).join(' + ');
      toast({ title: 'Fresh draft ready ✨', description: `AI regenerated your ${bits} copy.` });
    } catch (e) {
      toast({ title: "AI draft failed", description: e?.response?.data?.detail || e.message });
    }
    setAiBusy(false);
  };

  const toggleLock = async (channel) => {
    // channel: 'email' | 'sms' | 'smsNoEmail'
    const currentFlag = channel === 'email' ? tmpl.emailLocked : channel === 'sms' ? tmpl.smsLocked : tmpl.smsNoEmailLocked;
    const next = !currentFlag;
    const label = channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'SMS (no email)';
    try {
      const { data } = await client.post('/invitations/template/lock', { [channel]: next });
      setTmpl(data);
      toast({
        title: next ? `${label} locked as final` : `${label} unlocked`,
        description: next ? 'Regeneration and edits are disabled until you unlock.' : 'You can edit and regenerate again.',
      });
    } catch (e) {
      toast({ title: 'Could not update lock', description: e?.response?.data?.detail || e.message });
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset the invitation copy to the default branded template? This overwrites any current edits (except locked sections).')) return;
    try {
      const { data } = await client.post('/invitations/template/reset');
      setTmpl(data);
      toast({ title: 'Reset to default ✨', description: 'The branded template with photo, RSVP button and styling is back.' });
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const setPalette = async (palette) => {
    try {
      const { data } = await client.post('/invitations/template/palette', { palette });
      setTmpl(data);
      toast({ title: 'Palette applied ✨', description: 'Your email invitation has been re-skinned. Add your prose back with Generate or edit inline.' });
    } catch (e) {
      toast({ title: 'Could not switch palette', description: e?.response?.data?.detail || e.message });
    }
  };

  const runTestSend = async () => {
    setTestBusy(true);
    try {
      const body = { channel: testChannel };
      if (testChannel !== 'sms') body.email = testEmail || undefined;
      if (testChannel !== 'email') body.phone = testPhone || undefined;
      const { data } = await client.post('/invitations/test-send', body);
      const emailOk = !data.email || data.email.accepted;
      const smsOk = !data.sms || data.sms.accepted;
      if (data.email?.mocked || data.sms?.mocked) {
        toast({ title: 'Test-send simulated (no keys yet)', description: 'Once the email and SMS providers are wired up, real messages will fire.' });
      } else if (emailOk && smsOk) {
        toast({ title: 'Test message sent 🎉', description: 'Check your inbox / phone.' });
      } else {
        toast({ title: 'Test-send had errors', description: JSON.stringify(data), });
      }
      setLastResult(data);
    } catch (e) {
      toast({ title: 'Test-send failed', description: e?.response?.data?.detail || e.message });
    }
    setTestBusy(false);
  };

  const selectedItems = useMemo(() => {
    return guests
      .filter(g => selection[g.id] && selection[g.id] !== 'skip')
      .map(g => ({ guestId: g.id, channel: selection[g.id] }));
  }, [guests, selection]);

  // Combined delivery status for a guest — "opened" wins over "delivered"
  // wins over "sent" so the pill always reflects the furthest-along signal.
  const statusOf = (g) => {
    const email = g.email_opened_at ? 'opened' : g.email_status || null;
    const sms = g.sms_status || null;
    const rank = { opened: 5, delivered: 4, submitted: 3, sent: 3, failed: 2, bounced: 1, spam_complaint: 1 };
    const pick = [email, sms].filter(Boolean).sort((a, b) => (rank[b] || 0) - (rank[a] || 0))[0];
    return pick || 'unsent';
  };
  // Best-guess "when did this event happen" for the pill's inline timestamp.
  const statusTimeOf = (g, s) => {
    if (s === 'opened') return g.email_opened_at;
    if (s === 'delivered') return g.email_delivered_at || g.sms_delivered_at || g.email_last_event_at || g.sms_last_attempt_at;
    if (s === 'submitted' || s === 'sent') return g.email_sent_at || g.sms_sent_at || g.email_last_event_at || g.sms_last_attempt_at;
    if (s === 'bounced' || s === 'spam_complaint' || s === 'failed') return g.email_status_at || g.email_last_event_at || g.sms_last_attempt_at;
    return null;
  };
  const STATUS_PILL = {
    unsent:         { color: 'bg-transparent border border-[#D9CCBE] text-[#6A6A6A]', dot: 'border border-[#D9CCBE] bg-transparent', label: 'Not sent' },
    submitted:      { color: 'bg-[#FBF1E6] text-[#A15A2A]',                            dot: 'bg-[#EFC9B0]',                          label: 'Queued' },
    sent:           { color: 'bg-[#FBF1E6] text-[#A15A2A]',                            dot: 'bg-[#EFC9B0]',                          label: 'Sent' },
    delivered:      { color: 'bg-[#E6F1E6] text-[#2F5D2F]',                            dot: 'bg-[#4A7A4A]',                          label: 'Delivered' },
    opened:         { color: 'bg-[#F4E4DA] text-[#7B2C3B]',                            dot: 'bg-[#7B2C3B]',                          label: 'Opened' },
    bounced:        { color: 'bg-red-50 text-red-700',                                 dot: 'bg-red-500',                            label: 'Bounced' },
    spam_complaint: { color: 'bg-red-50 text-red-700',                                 dot: 'bg-red-500',                            label: 'Spam' },
    failed:         { color: 'bg-[#F6E8E1] text-[#8B4A32]',                            dot: 'bg-[#A0644C]',                          label: 'Failed' },
  };

  // Filtered guest list — used by BOTH the inline picker and the full-screen
  // recipient manager. Keep the source of truth here so nothing drifts.
  const filteredGuests = useMemo(() => {
    const q = pkQuery.trim().toLowerCase();
    return guests.filter((g) => {
      if (q) {
        const hay = `${g.name || ''} ${g.email || ''} ${g.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (pkSide !== 'all' && (g.side || '') !== pkSide) return false;
      if (pkGroup !== 'all' && (g.group || '') !== pkGroup) return false;
      if (pkHasEmail && !g.email) return false;
      if (pkHasPhone && !g.phone) return false;
      if (pkStatus !== 'all') {
        const s = statusOf(g);
        if (pkStatus === 'unsent' && s !== 'unsent') return false;
        if (pkStatus === 'delivered' && !['delivered', 'opened'].includes(s)) return false;
        if (pkStatus === 'opened' && s !== 'opened') return false;
        if (pkStatus === 'bounced' && !['bounced', 'spam_complaint', 'failed'].includes(s)) return false;
      }
      if (pkRsvp !== 'all') {
        const r = g.rsvp;
        if (pkRsvp === 'yes' && r !== 'Yes') return false;
        if (pkRsvp === 'no' && r !== 'No') return false;
        if (pkRsvp === 'pending' && (r === 'Yes' || r === 'No')) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests, pkQuery, pkSide, pkGroup, pkHasEmail, pkHasPhone, pkStatus, pkRsvp]);

  // Running summary of the current selection so couples always know exactly
  // what's about to fire before they click Review & send.
  const selSummary = useMemo(() => {
    let email = 0, sms = 0, both = 0;
    Object.values(selection).forEach((v) => {
      if (v === 'email') email++;
      else if (v === 'sms') sms++;
      else if (v === 'both') both++;
    });
    return { email, sms, both, total: email + sms + both };
  }, [selection]);

  const clearFilters = () => {
    setPkQuery(''); setPkSide('all'); setPkGroup('all');
    setPkHasEmail(false); setPkHasPhone(false); setPkStatus('all'); setPkRsvp('all');
  };

  const saveCurrentSegment = () => {
    const name = window.prompt('Name this segment (e.g. "Bride\'s family", "Out-of-towners", "Ceremony only"):');
    if (!name || !name.trim()) return;
    const seg = {
      id: `seg-${Date.now()}`,
      name: name.trim(),
      filters: {
        query: pkQuery, side: pkSide, group: pkGroup,
        hasEmail: pkHasEmail, hasPhone: pkHasPhone, status: pkStatus,
      },
    };
    setSavedSegments((prev) => [...prev, seg]);
    toast({ title: 'Segment saved', description: `"${seg.name}" will show up in your filter menu.` });
  };
  const loadSegment = (seg) => {
    const f = seg.filters || {};
    setPkQuery(f.query || '');
    setPkSide(f.side || 'all');
    setPkGroup(f.group || 'all');
    setPkHasEmail(!!f.hasEmail);
    setPkHasPhone(!!f.hasPhone);
    setPkStatus(f.status || 'all');
    toast({ title: `Loaded "${seg.name}"`, description: 'Filters applied. Use bulk actions to pick channels.' });
  };
  const deleteSegment = (id) => {
    setSavedSegments((prev) => prev.filter((s) => s.id !== id));
  };

  // Bulk: apply a channel to every guest currently visible under the active
  // filter. Respects each guest's contact channels (won't email someone with
  // no email, won't SMS someone with no phone).
  const applyBulkToFiltered = (channel) => {
    const next = { ...selection };
    filteredGuests.forEach((g) => {
      if (channel === 'skip') { delete next[g.id]; return; }
      const hasE = !!g.email, hasP = !!g.phone;
      if (channel === 'email' && hasE) next[g.id] = next[g.id] === 'sms' ? 'both' : 'email';
      if (channel === 'sms' && hasP) next[g.id] = next[g.id] === 'email' ? 'both' : 'sms';
      if (channel === 'both') {
        if (hasE && hasP) next[g.id] = 'both';
        else if (hasE) next[g.id] = 'email';
        else if (hasP) next[g.id] = 'sms';
      }
    });
    setSelection(next);
  };

  const toggleChannel = (guestId, channel) => {
    setSelection((prev) => {
      const current = prev[guestId] || 'skip';
      if (current === 'skip') return { ...prev, [guestId]: channel };
      if (current === channel) return { ...prev, [guestId]: 'skip' };
      if ((current === 'email' && channel === 'sms') || (current === 'sms' && channel === 'email')) {
        return { ...prev, [guestId]: 'both' };
      }
      if (current === 'both') return { ...prev, [guestId]: channel === 'email' ? 'sms' : 'email' };
      return { ...prev, [guestId]: channel };
    });
  };

  const selectAllEmail = () => {
    const next = { ...selection };
    guests.forEach(g => { if (g.email) next[g.id] = next[g.id] === 'sms' ? 'both' : 'email'; });
    setSelection(next);
  };
  const selectAllSms = () => {
    const next = { ...selection };
    guests.forEach(g => { if (g.phone) next[g.id] = next[g.id] === 'email' ? 'both' : 'sms'; });
    setSelection(next);
  };
  const selectAllBoth = () => {
    const next = { ...selection };
    guests.forEach(g => {
      const hasE = !!g.email, hasP = !!g.phone;
      if (hasE && hasP) next[g.id] = 'both';
      else if (hasE) next[g.id] = 'email';
      else if (hasP) next[g.id] = 'sms';
    });
    setSelection(next);
  };

  // "Not yet invited" = we have never fired an email or SMS for this guest
  // (email_status + sms_status are set by /invitations/send). Perfect for
  // sending catch-up invites to guests added AFTER an earlier batch went out.
  const selectNotInvited = () => {
    const next = { ...selection };
    guests.forEach(g => {
      const alreadyEmailed = !!g.email_status;
      const alreadyTexted = !!g.sms_status;
      const hasE = !!g.email, hasP = !!g.phone;
      // Only pick channels that (a) the guest supports AND (b) haven't already fired.
      const pickE = hasE && !alreadyEmailed;
      const pickS = hasP && !alreadyTexted;
      if (pickE && pickS) next[g.id] = 'both';
      else if (pickE) next[g.id] = 'email';
      else if (pickS) next[g.id] = 'sms';
      else delete next[g.id];  // fully covered — leave them out
    });
    setSelection(next);
  };

  const clearAll = () => setSelection({});

  const doSend = async () => {
    setSendBusy(true);
    try {
      const { data } = await client.post('/invitations/send', { items: selectedItems, confirm: true });
      setLastResult(data);
      const sent = data.results.filter(r => (r.email?.accepted) || (r.sms?.accepted)).length;
      const mocked = data.results.some(r => r.email?.mocked || r.sms?.mocked);
      if (mocked) {
        toast({ title: 'Simulated send only', description: 'Email and SMS providers aren\'t configured yet — nothing was actually sent.' });
      } else {
        toast({ title: `${sent} of ${data.count} sent`, description: 'Guests should receive their invites shortly.' });
      }
    } catch (e) {
      // Backend may return `detail` as either a string OR a plan-enforcement
      // object ({code, limit_type, message, ...}). Rendering the object as a
      // React child crashes the page — so coerce to a string first.
      const detail = e?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail?.message || e.message || 'Something went wrong. Please try again.');
      toast({ title: 'Send failed', description: message });
    }
    setSendBusy(false);
    setConfirming(false);
  };

  if (loading || !tmpl) {
    return <div className="flex items-center justify-center py-20 text-[#6A6A6A]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invitations…</div>;
  }

  const emailReady = !!status?.email?.configured;
  const smsReady = !!status?.sms?.configured;

  const emailPreview = renderPreview(tmpl.email.htmlBody, previewCtx);
  const smsPreview = renderPreview(tmpl.sms.content, previewCtx);

  return (
    <div data-testid="invitations-page" className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="section-eyebrow">Invitations</p>
          <h2 className="font-serif text-3xl text-[#2A2A2A] mt-1 flex items-center gap-3"><AppIcon feature="invitations" size="lg" reveal testId="section-icon-invitations" />Send your wedding invitations</h2>
          <p className="text-sm text-[#6A6A6A] mt-1 max-w-xl">
            Draft with AI, preview live, test-send to yourself first — then approve exactly who receives what. Nothing goes out without your explicit confirmation.
          </p>
        </div>
      </div>

      <HostSmsAuthorization hideWhenAuthorized />

      {/* Integration status banner */}
      <div data-testid="invitations-integrations" className={`rounded-2xl p-4 border ${(emailReady || smsReady) ? 'bg-[#EAF2E5] border-[#C9DDBB]' : 'bg-[#FBF1EA] border-[#EFC9B0]'}`}>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email: {emailReady ? <span className="text-[#4A7A4A] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Postmark live · from {status?.email?.fromEmail}</span> : <span className="text-[#A0644C] inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Simulated only — add POSTMARK_TOKEN + POSTMARK_FROM_EMAIL</span>}</span>
          <span className="text-[#EFC9B0]">·</span>
          <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> SMS: {smsReady ? <span className="text-[#4A7A4A] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Live · from {status?.sms?.fromNumber}</span> : <span className="text-[#A0644C] inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Simulated only — SMS provider not configured yet</span>}</span>
        </div>
      </div>

      {/* Legacy template warning banner */}
      {tmpl?.emailIsLegacy && !tmpl.emailLocked && (
        <div data-testid="legacy-email-banner" className="rounded-2xl p-4 border-2 border-[#7B2C3B] bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7B2C3B] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[#FBF7F2]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#2A2A2A]">Upgrade to the new invitation design ✨</p>
              <p className="text-sm text-[#4A4A4A] mt-1">
                Your current email is using an older layout without the couple photo, engraved date block, or styled RSVP button.
                Click below to apply the beautiful new wedding-invitation shell — your custom subject line stays as-is.
              </p>
              <button
                data-testid="legacy-upgrade-btn"
                onClick={resetToDefault}
                className="btn-primary mt-3 text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" /> Apply the new design
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Palette picker */}
      {tmpl?.palettes && (
        <div data-testid="palette-picker" className="rounded-2xl p-5 border border-[#EFE3D8] bg-white">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <p className="section-eyebrow text-[#7B2C3B]">Style</p>
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Invitation palette
                <InfoTip testId="info-palette" title="Colour theme for your invitation email">
                  <p>Pick a palette and we&apos;ll re-skin the invitation email with matching colours, hairlines and accents. The layout (photo, date block, RSVP button) stays the same.</p>
                  <p><strong>Heads up</strong>: switching a palette resets the prose to the branded default. If you have custom AI-drafted words you want to keep, save a copy first — or lock the email before switching, then unlock after.</p>
                </InfoTip>
              </h3>
            </div>
            {tmpl.emailLocked && (
              <span className="text-[11px] text-[#7B2C3B] inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> Unlock the email to change palette
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {tmpl.palettes.map((pal) => {
              const active = tmpl.emailPalette === pal.id;
              return (
                <button
                  key={pal.id}
                  data-testid={`palette-${pal.id}`}
                  disabled={tmpl.emailLocked}
                  onClick={() => setPalette(pal.id)}
                  className={`relative p-3 rounded-xl border-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${active ? 'border-[#7B2C3B] shadow-sm' : 'border-[#EFE3D8] hover:border-[#B8896C]'}`}
                  style={{ background: pal.wash }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full border border-white" style={{ background: pal.accent }} />
                    <span className="text-sm font-medium text-[#2A2A2A]">{pal.name}</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ background: pal.accent }} />
                  {active && (
                    <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#7B2C3B] flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Editor */}
        <div className="lg:col-span-2 space-y-5">
          {/* AI drafter */}
          <section className="soft-card rounded-2xl p-5 bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2] border border-[#EFE3D8]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#7B2C3B]" /> Draft with AI
                <InfoTip testId="info-ai-drafter" title="Let AI write your invitation">
                  <p>Pick a tone, fill in a few details, and our AI drafts a warm invitation email + SMS in your voice.</p>
                  <p><strong>Fields explained</strong>:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><strong>Tone</strong>: sets the vibe (warm, playful, formal, poetic)</li>
                    <li><strong>Ceremony start time</strong>: e.g. "4:30 PM"</li>
                    <li><strong>Food style</strong>: "Family-style Italian", "Buffet BBQ", etc.</li>
                    <li><strong>Entertainment</strong>: "Live jazz then DJ", "Mariachi &amp; salsa dancing"</li>
                    <li><strong>Attire</strong>: "Cocktail attire", "Black-tie", "Garden semi-formal"</li>
                    <li><strong>Mood</strong>: what you want guests to feel — "Intimate", "Loud dance floor", "Sunset romantic"</li>
                    <li><strong>Other notes</strong>: anything else — kids welcome, no phones during ceremony, dietary options, etc.</li>
                  </ul>
                  <p>You can regenerate as many times as you like. Once you love a version, hit <strong>Lock as final</strong> below to freeze it.</p>
                </InfoTip>
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {TONES.map((t) => (
                <button key={t.id} data-testid={`tone-${t.id}`} onClick={() => setTone(t.id)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tone === t.id ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}>{t.label}</button>
              ))}
            </div>
            <textarea
              data-testid="ai-notes"
              placeholder="Anything else the AI should mention — e.g. 'we met salsa dancing, kids welcome, garden shoes recommended'"
              value={details.otherNotes}
              onChange={(e) => setDetails((p) => ({ ...p, otherNotes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B] mb-3"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <AiField testId="ai-time" label="Ceremony start time" placeholder="4:30 PM" value={details.startTime} onChange={(v) => setDetails((p) => ({ ...p, startTime: v }))} />
              <AiField testId="ai-food" label="Food / dinner style" placeholder="Family-style Italian" value={details.foodStyle} onChange={(v) => setDetails((p) => ({ ...p, foodStyle: v }))} />
              <AiField testId="ai-entertainment" label="Entertainment" placeholder="Live jazz then DJ" value={details.entertainment} onChange={(v) => setDetails((p) => ({ ...p, entertainment: v }))} />
              <AiField testId="ai-attire" label="Attire / dress code" placeholder="Cocktail attire" value={details.attire} onChange={(v) => setDetails((p) => ({ ...p, attire: v }))} />
              <AiField testId="ai-mood" label="Mood you want" placeholder="Intimate, loud dance floor" value={details.mood} onChange={(v) => setDetails((p) => ({ ...p, mood: v }))} />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-[#9A8A7E] max-w-md">
                {tmpl.emailLocked && tmpl.smsLocked
                  ? 'Both email and SMS are locked as final. Unlock at least one to regenerate.'
                  : tmpl.emailLocked
                    ? 'Email is locked. Regenerating will only refresh the SMS.'
                    : tmpl.smsLocked
                      ? 'SMS is locked. Regenerating will only refresh the email.'
                      : "Regenerate rewrites both. Lock a version below once you're happy with it."}
              </p>
              <div className="flex items-center gap-2">
                <button data-testid="reset-default" onClick={resetToDefault} disabled={tmpl.emailLocked && tmpl.smsLocked} className="text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 bg-white border border-[#EFE3D8] text-[#3A3A3A] hover:border-[#B8896C] disabled:opacity-40 disabled:cursor-not-allowed">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset to default
                </button>
                <button data-testid="ai-generate" disabled={aiBusy || (tmpl.emailLocked && tmpl.smsLocked)} onClick={generateAI} className="btn-primary text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {aiBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4" /> Generate fresh copy</>}
                </button>
              </div>
            </div>
          </section>

          {/* Couple photo (shared with wedding site) */}
          <section data-testid="tmpl-couple-photo" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-md">
                <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Couple photo
                  <InfoTip testId="info-couple-photo" title="One photo, two places">
                    <p>Upload a portrait of the two of you. It appears in two places automatically:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>Centered at the top of every invitation email</li>
                      <li>In the "Our story" section of your public wedding site</li>
                    </ul>
                    <p><strong>Best photos</strong>: portrait orientation (taller than wide), your faces well-lit and roughly centered. Engagement shots work beautifully.</p>
                    <p>JPG, PNG or WEBP up to 8 MB. You can replace it any time.</p>
                  </InfoTip>
                </h3>
                <p className="text-xs text-[#6A6A6A] mt-1">
                  This photo appears at the top of every invitation email and in the Story section of your public wedding site — one upload, both places.
                </p>
              </div>
              <div className="w-full sm:w-64">
                <PhotoUploader
                  testId="invitations-couple-photo"
                  label=""
                  aspect="portrait"
                  value={couplePhoto || ''}
                  onChange={saveCouplePhoto}
                />
              </div>
            </div>
          </section>

          {/* Email template */}
          <section data-testid="tmpl-email" className={`soft-card rounded-2xl p-5 border ${tmpl.emailLocked ? 'bg-[#EAF2E5] border-[#C9DDBB]' : 'bg-white border-[#EFE3D8]'}`}>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email invitation
                <InfoTip testId="info-email-tmpl" title="Your invitation email">
                  <p>This is what lands in each guest&apos;s inbox. The <strong>Subject</strong> shows in their inbox list; the <strong>Body</strong> renders when they open it.</p>
                  <p>We wrap your text in a beautiful branded shell — couple photo, "RSVP now" button and site link are added automatically. You only need to write the words.</p>
                  <p><strong>Merge fields</strong>: type things like <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code> and they&apos;ll be replaced with each guest&apos;s details on send.</p>
                  <p>The <strong>Plain-text fallback</strong> is what guests see if their email client blocks HTML (rare). Keep it in sync roughly with the HTML.</p>
                  <p>Happy with it? Hit <strong>Lock as final</strong> to freeze this exact version.</p>
                </InfoTip>
                {tmpl.emailLocked && (
                  <span data-testid="email-locked-badge" className="ml-1 text-[10px] uppercase tracking-wider bg-[#4A7A4A] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> Final
                  </span>
                )}
              </h3>
              <button
                data-testid="email-lock-toggle"
                onClick={() => toggleLock('email')}
                className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border transition-colors ${tmpl.emailLocked ? 'bg-white border-[#C9DDBB] text-[#4A7A4A] hover:bg-[#F4E4DA]' : 'bg-[#7B2C3B] border-[#7B2C3B] text-white hover:opacity-90'}`}
              >
                {tmpl.emailLocked ? <><Unlock className="w-3.5 h-3.5" /> Unlock to edit</> : <><Lock className="w-3.5 h-3.5" /> Lock as final</>}
              </button>
            </div>
            <label className="text-xs text-[#6A6A6A]">Subject</label>
            <input
              data-testid="tmpl-email-subject"
              readOnly={tmpl.emailLocked}
              value={tmpl.email.subject}
              onChange={(e) => setTmpl({ ...tmpl, email: { ...tmpl.email, subject: e.target.value } })}
              onBlur={(e) => !tmpl.emailLocked && saveTemplate({ email: { ...tmpl.email, subject: e.target.value } })}
              className={`mt-1 mb-3 w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${tmpl.emailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
            />

            {/* Plain-English ↔ Advanced HTML tabs — keeps the friendly editor
                as the default while still letting power users touch raw HTML. */}
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <label className="text-xs text-[#6A6A6A]">Body</label>
              <div className="inline-flex rounded-full bg-[#FBF7F2] border border-[#EFE3D8] p-0.5 text-[11px]">
                <button
                  type="button"
                  data-testid="tmpl-email-mode-plain"
                  onClick={() => setEmailMode('plain')}
                  className={`px-3 py-1 rounded-full transition-all ${emailMode === 'plain' ? 'bg-[#7B2C3B] text-white' : 'text-[#6A6A6A]'}`}
                >
                  Plain English
                </button>
                <button
                  type="button"
                  data-testid="tmpl-email-mode-html"
                  onClick={() => setEmailMode('html')}
                  className={`px-3 py-1 rounded-full transition-all ${emailMode === 'html' ? 'bg-[#7B2C3B] text-white' : 'text-[#6A6A6A]'}`}
                >
                  Advanced HTML
                </button>
              </div>
            </div>
            {emailMode === 'plain' ? (
              <>
                <textarea
                  data-testid="tmpl-email-prose"
                  readOnly={tmpl.emailLocked}
                  rows={8}
                  value={tmpl.email.prose || ''}
                  onChange={(e) => setTmpl({ ...tmpl, email: { ...tmpl.email, prose: e.target.value } })}
                  onBlur={(e) => !tmpl.emailLocked && saveTemplate({ email: { ...tmpl.email, prose: e.target.value } })}
                  placeholder={"Type each paragraph as you'd say it out loud. Leave a blank line between paragraphs.\n\nMerge fields like {firstName} and {coupleNames} still work."}
                  className={`mt-1 mb-1 w-full px-3 py-2 rounded-lg border text-sm leading-relaxed focus:outline-none ${tmpl.emailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
                />
                <p className="text-[10px] text-[#9A8A7E] mb-3">
                  The first paragraph renders italic (like an intro). Blank lines start new paragraphs. Your styling, colours and the RSVP button below stay locked in.
                </p>
                <label className="text-xs text-[#6A6A6A]" htmlFor="tmpl-email-close">Sign-off (closing line)</label>
                <input
                  id="tmpl-email-close"
                  data-testid="tmpl-email-close"
                  readOnly={tmpl.emailLocked}
                  value={tmpl.email.emailClose || 'With so much love,\n{coupleNames}'}
                  onChange={(e) => setTmpl({ ...tmpl, email: { ...tmpl.email, emailClose: e.target.value } })}
                  onBlur={(e) => !tmpl.emailLocked && saveTemplate({ email: { ...tmpl.email, emailClose: e.target.value } })}
                  placeholder="With so much love,\n{coupleNames}"
                  className={`mt-1 mb-1 w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${tmpl.emailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
                />
                <p className="text-[10px] text-[#9A8A7E] mb-3">
                  Shows in both the styled email and plain-text fallback. Use <code>{'{coupleNames}'}</code> to insert your names — capitalise however you like.
                </p>
              </>
            ) : (
              <>
                <textarea
                  data-testid="tmpl-email-html"
                  readOnly={tmpl.emailLocked}
                  rows={10}
                  value={tmpl.email.htmlBody}
                  onChange={(e) => setTmpl({ ...tmpl, email: { ...tmpl.email, htmlBody: e.target.value } })}
                  onBlur={(e) => !tmpl.emailLocked && saveTemplate({ email: { ...tmpl.email, htmlBody: e.target.value } })}
                  className={`mt-1 mb-1 w-full px-3 py-2 rounded-lg border text-xs font-mono focus:outline-none ${tmpl.emailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
                />
                <p className="text-[10px] text-[#B8896C] mb-3">
                  Heads up — edits here override the plain-English tab. Only touch this if you&apos;re comfortable with HTML and inline CSS for email.
                </p>
              </>
            )}
            <label className="text-xs text-[#6A6A6A]">Plain-text fallback</label>
            <textarea
              data-testid="tmpl-email-text"
              readOnly={tmpl.emailLocked}
              rows={4}
              value={tmpl.email.textBody}
              onChange={(e) => setTmpl({ ...tmpl, email: { ...tmpl.email, textBody: e.target.value } })}
              onBlur={(e) => !tmpl.emailLocked && saveTemplate({ email: { ...tmpl.email, textBody: e.target.value } })}
              className={`mt-1 w-full px-3 py-2 rounded-lg border text-xs focus:outline-none ${tmpl.emailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
            />
            <div className="text-[10px] text-[#9A8A7E] mt-2">Merge fields: {MERGE_HINTS.join(' · ')}</div>
          </section>

          {/* SMS template */}
          <section data-testid="tmpl-sms" className={`soft-card rounded-2xl p-5 border ${tmpl.smsLocked ? 'bg-[#EAF2E5] border-[#C9DDBB]' : 'bg-white border-[#EFE3D8]'}`}>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> SMS invitation
                <InfoTip testId="info-sms-tmpl" title="The text-message version">
                  <p>A short text sent to any guest with a phone number. Great as a nudge alongside the email — some guests read texts faster than inboxes.</p>
                  <p><strong>Rules of thumb</strong>:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Under 300 characters keeps it in a single message</li>
                    <li>Always includes <code>{'{rsvpLink}'}</code> so they can reply in one tap</li>
                    <li>We recommend leading with "You&apos;re invited!" and pointing to the email for full details</li>
                  </ul>
                  <p><strong>Merge fields</strong> like <code>{'{firstName}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{weddingTime}'}</code> get filled in at send time.</p>
                </InfoTip>
                {tmpl.smsLocked && (
                  <span data-testid="sms-locked-badge" className="ml-1 text-[10px] uppercase tracking-wider bg-[#4A7A4A] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> Final
                  </span>
                )}
              </h3>
              <button
                data-testid="sms-lock-toggle"
                onClick={() => toggleLock('sms')}
                className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border transition-colors ${tmpl.smsLocked ? 'bg-white border-[#C9DDBB] text-[#4A7A4A] hover:bg-[#F4E4DA]' : 'bg-[#7B2C3B] border-[#7B2C3B] text-white hover:opacity-90'}`}
              >
                {tmpl.smsLocked ? <><Unlock className="w-3.5 h-3.5" /> Unlock to edit</> : <><Lock className="w-3.5 h-3.5" /> Lock as final</>}
              </button>
            </div>
            <textarea
              data-testid="tmpl-sms-content"
              readOnly={tmpl.smsLocked}
              rows={3}
              value={tmpl.sms.content}
              onChange={(e) => setTmpl({ ...tmpl, sms: { ...tmpl.sms, content: e.target.value } })}
              onBlur={(e) => !tmpl.smsLocked && saveTemplate({ sms: { content: e.target.value } })}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${tmpl.smsLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]'}`}
            />
            <div className="text-[10px] text-[#9A8A7E] mt-1 flex justify-between">
              <span>{tmpl.sms.content.length} / 1600 chars · SMS-only</span>
              <span>{MERGE_HINTS.join(' · ')}</span>
            </div>
          </section>

          {/* ---------- SMS for guests without an email on file ---------- */}
          <section
            data-testid="tmpl-sms-no-email"
            className={`soft-card rounded-2xl p-5 border ${!tmpl.smsNoEmailEnabled ? 'bg-[#F9F5F0] border-[#EFE3D8] opacity-60' : (tmpl.smsNoEmailLocked ? 'bg-[#EAF2E5] border-[#C9DDBB]' : 'bg-white border-[#EFE3D8]')}`}
          >
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> SMS invitation <span className="text-[#B8896C] italic">(for guests without an email)</span>
                <InfoTip testId="info-sms-no-email" title="Auto-collects missing emails">
                  <p>Sent to any guest you SMS who <strong>doesn&apos;t yet have an email on file</strong>. Asks them to reply with their email address.</p>
                  <p>If you&apos;re collecting missing emails on the <strong>RSVP page</strong> instead, turn this off — phone-only guests will receive your standard SMS (with the RSVP link) so they can drop the email in when they RSVP.</p>
                </InfoTip>
                {tmpl.smsNoEmailEnabled && tmpl.smsNoEmailLocked && (
                  <span data-testid="sms-noemail-locked-badge" className="ml-1 text-[10px] uppercase tracking-wider bg-[#4A7A4A] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> Final
                  </span>
                )}
                {!tmpl.smsNoEmailEnabled && (
                  <span data-testid="sms-noemail-disabled-badge" className="ml-1 text-[10px] uppercase tracking-wider bg-[#9A8A7E] text-white px-2 py-0.5 rounded-full">Off</span>
                )}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] bg-white cursor-pointer">
                  <input
                    data-testid="sms-noemail-enabled-toggle"
                    type="checkbox"
                    checked={!!tmpl.smsNoEmailEnabled}
                    onChange={async (e) => {
                      try {
                        const { data } = await client.post('/invitations/template/lock', { smsNoEmailEnabled: e.target.checked });
                        setTmpl(data);
                        toast({
                          title: e.target.checked ? 'Second SMS is now ON' : 'Second SMS is now OFF',
                          description: e.target.checked ? 'Guests without an email will get this SMS asking them to reply with their email.' : "Guests without an email will receive your standard SMS. Collect the email via the RSVP page instead.",
                        });
                      } catch (err) {
                        toast({ title: 'Could not update', description: err?.response?.data?.detail || err.message });
                      }
                    }}
                    className="accent-[#7B2C3B] w-3.5 h-3.5"
                  />
                  Use this template
                </label>
                <button
                  data-testid="sms-noemail-lock-toggle"
                  disabled={!tmpl.smsNoEmailEnabled}
                  onClick={() => toggleLock('smsNoEmail')}
                  className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border transition-colors ${!tmpl.smsNoEmailEnabled ? 'bg-white border-[#EFE3D8] text-[#9A8A7E] cursor-not-allowed' : (tmpl.smsNoEmailLocked ? 'bg-white border-[#C9DDBB] text-[#4A7A4A] hover:bg-[#F4E4DA]' : 'bg-[#7B2C3B] border-[#7B2C3B] text-white hover:opacity-90')}`}
                >
                  {tmpl.smsNoEmailLocked ? <><Unlock className="w-3.5 h-3.5" /> Unlock to edit</> : <><Lock className="w-3.5 h-3.5" /> Lock as final</>}
                </button>
              </div>
            </div>
            <textarea
              data-testid="tmpl-sms-no-email-content"
              readOnly={tmpl.smsNoEmailLocked || !tmpl.smsNoEmailEnabled}
              rows={4}
              value={tmpl.smsNoEmail?.content || ''}
              onChange={(e) => setTmpl({ ...tmpl, smsNoEmail: { ...(tmpl.smsNoEmail || {}), content: e.target.value } })}
              onBlur={(e) => !tmpl.smsNoEmailLocked && tmpl.smsNoEmailEnabled && saveTemplate({ smsNoEmail: { content: e.target.value } })}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${!tmpl.smsNoEmailEnabled ? 'border-[#EFE3D8] bg-[#F9F5F0] text-[#9A8A7E] cursor-not-allowed' : (tmpl.smsNoEmailLocked ? 'border-[#C9DDBB] bg-[#F4F9EE] text-[#3A3A3A] cursor-not-allowed' : 'border-[#EFE3D8] bg-white focus:border-[#7B2C3B]')}`}
            />
            <div className="text-[10px] text-[#9A8A7E] mt-1 flex justify-between">
              <span>{(tmpl.smsNoEmail?.content || '').length} / 1600 chars · {tmpl.smsNoEmailEnabled ? 'Auto-sent to phone-only guests' : 'Off — phone-only guests get the standard SMS above'}</span>
              <span>{MERGE_HINTS.join(' · ')}</span>
            </div>
          </section>

          {/* Guest picker */}
          <section data-testid="tmpl-guests" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <Users className="w-4 h-4" /> Who receives this?
                <InfoTip testId="info-guest-picker" title="Choose exactly who gets invited">
                  <p>Only guests you pick here will receive an invitation. Nothing goes out until you hit <strong>Review &amp; send</strong> and confirm.</p>
                  <p><strong>Per-guest channels</strong>:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><strong>Email</strong> — only enabled if the guest has an email address</li>
                    <li><strong>Text</strong> — only enabled if they have a phone number</li>
                    <li>Tap both to send email + SMS to that guest</li>
                  </ul>
                  <p>Tap <strong>⤢ Full-screen</strong> for a wide table with search, filters and bulk actions — helpful once your guest list grows.</p>
                </InfoTip>
              </h3>
              <button
                data-testid="open-recipient-mgr"
                onClick={() => setShowRecipientMgr(true)}
                className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#611E2E] inline-flex items-center gap-1.5"
                title="Open the full-screen recipient manager"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Full screen
              </button>
            </div>

            {/* Bulk-select chips */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <button
                data-testid="select-not-invited"
                onClick={selectNotInvited}
                title="Select only guests we've never emailed or texted — perfect for catching guests added after your first send-out."
                className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#611E2E] inline-flex items-center gap-1"
              >
                <MailPlus className="w-3.5 h-3.5" /> Only not yet invited
                {(() => {
                  const n = guests.filter((g) => (!g.email_status && !!g.email) || (!g.sms_status && !!g.phone)).length;
                  return n > 0 ? <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FBF7F2] text-[#7B2C3B]">{n}</span> : null;
                })()}
              </button>
              <button data-testid="select-all-email" onClick={selectAllEmail} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">Select all with email</button>
              <button data-testid="select-all-sms" onClick={selectAllSms} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">Select all with text</button>
              <button data-testid="select-all-both" onClick={selectAllBoth} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">Everyone</button>
              <button data-testid="clear-all" onClick={clearAll} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">Clear</button>
            </div>

            {/* Inline search + filters — mirror the modal so muscle memory transfers */}
            <div className="mb-3 space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E]" />
                <input
                  data-testid="picker-search"
                  value={pkQuery}
                  onChange={(e) => setPkQuery(e.target.value)}
                  placeholder="Search by name, email or phone"
                  className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FBF7F2] border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <select data-testid="picker-side" value={pkSide} onChange={(e) => setPkSide(e.target.value)} className="px-2 py-1 rounded-full border border-[#EFE3D8] bg-transparent">
                  <option value="all">All sides</option>
                  <option>Bride</option><option>Groom</option><option>Both</option>
                </select>
                <select data-testid="picker-group" value={pkGroup} onChange={(e) => setPkGroup(e.target.value)} className="px-2 py-1 rounded-full border border-[#EFE3D8] bg-transparent">
                  <option value="all">All groups</option>
                  <option>Family</option><option>Friends</option><option>Work</option><option>Other</option>
                </select>
                <select data-testid="picker-status" value={pkStatus} onChange={(e) => setPkStatus(e.target.value)} className="px-2 py-1 rounded-full border border-[#EFE3D8] bg-transparent">
                  <option value="all">Any status</option>
                  <option value="unsent">Not sent yet</option>
                  <option value="delivered">Delivered</option>
                  <option value="opened">Opened</option>
                  <option value="bounced">Bounced / failed</option>
                </select>
                <button
                  onClick={() => setPkHasEmail((v) => !v)}
                  className={`px-2 py-1 rounded-full border ${pkHasEmail ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8]'}`}
                >Has email</button>
                <button
                  onClick={() => setPkHasPhone((v) => !v)}
                  className={`px-2 py-1 rounded-full border ${pkHasPhone ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8]'}`}
                >Has phone</button>
                {(pkQuery || pkSide !== 'all' || pkGroup !== 'all' || pkHasEmail || pkHasPhone || pkStatus !== 'all') && (
                  <button onClick={clearFilters} className="px-2 py-1 text-[#7B2C3B] hover:underline inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear filters
                  </button>
                )}
              </div>
            </div>

            {guests.length === 0 ? (
              <p className="text-sm text-[#6A6A6A]">
                No guests yet — head to the <span className="text-[#7B2C3B] font-medium">Guests</span> tab and add a few with email addresses first.
              </p>
            ) : filteredGuests.length === 0 ? (
              <p className="text-sm text-[#6A6A6A] py-6 text-center">No guests match those filters. <button onClick={clearFilters} className="text-[#7B2C3B] underline">Clear filters</button>.</p>
            ) : (
              <div className="divide-y divide-[#EFE3D8] max-h-96 overflow-y-auto -mx-2">
                {filteredGuests.map((g) => {
                  const pick = selection[g.id] || 'skip';
                  const s = statusOf(g);
                  const pill = STATUS_PILL[s] || STATUS_PILL.unsent;
                  const evtTime = statusTimeOf(g, s);
                  const evtStr = fmtDeliveryTime(evtTime);
                  const detail = buildDeliveryTooltip(g);
                  const tooltip = detail ? `${pill.label}\n\n${detail}` : pill.label;
                  return (
                    <div key={g.id} data-testid={`guest-row-${g.id}`} className="flex items-center justify-between py-2 px-2 gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#2A2A2A] truncate inline-flex items-center gap-1.5 flex-wrap">
                          {g.name || 'Unnamed'}
                          {!g.email && g.phone && (
                            <span
                              data-testid={`needs-email-pill-${g.id}`}
                              title="This guest doesn't have an email. If you SMS them, they'll get the 'reply with your email' version — and we'll auto-email them once they respond."
                              className="text-[9px] uppercase tracking-wider bg-[#F4E4DA] text-[#7B2C3B] border border-[#B8896C] px-1.5 py-0.5 rounded-full"
                            >
                              Needs email
                            </span>
                          )}
                          <span
                            data-testid={`row-status-${g.id}`}
                            title={tooltip}
                            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${pill.color}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full inline-block ${pill.dot}`} />
                            {pill.label}
                          </span>
                        </p>
                        <p className="text-[10px] text-[#9A8A7E] truncate">
                          {g.email || 'no email'} {g.phone ? `· ${g.phone}` : ''}
                          {evtStr && <span data-testid={`row-status-time-${g.id}`} className="ml-1">· {evtStr}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          data-testid={`pick-email-${g.id}`}
                          disabled={!g.email}
                          onClick={() => toggleChannel(g.id, 'email')}
                          className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                            (pick === 'email' || pick === 'both') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8]'
                          } ${!g.email ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <Mail className="w-3 h-3" /> Email
                        </button>
                        <button
                          data-testid={`pick-sms-${g.id}`}
                          disabled={!g.phone}
                          onClick={() => toggleChannel(g.id, 'sms')}
                          className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                            (pick === 'sms' || pick === 'both') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8]'
                          } ${!g.phone ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <MessageSquare className="w-3 h-3" /> Text
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-[#EFE3D8] pt-3 gap-3 flex-wrap">
              <p data-testid="selection-summary" className="text-xs text-[#6A6A6A]">
                <strong className="text-[#2A2A2A]">{selectedItems.length}</strong> selected
                {selSummary.total > 0 && (
                  <span className="ml-1 text-[#9A8A7E]">
                    · {selSummary.email} email · {selSummary.sms} text · {selSummary.both} both
                  </span>
                )}
              </p>
              <button
                data-testid="send-open-confirm"
                disabled={selectedItems.length === 0}
                onClick={() => setConfirming(true)}
                className={`btn-primary text-sm px-5 py-2 rounded-full inline-flex items-center gap-2 ${selectedItems.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Send className="w-4 h-4" /> Review &amp; send
              </button>
            </div>
          </section>
        </div>

        {/* RIGHT: Preview + Test-send */}
        <div className="space-y-5">
          <section data-testid="test-send-panel" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              <Eye className="w-4 h-4" /> Test send to me
              <InfoTip testId="info-test-send" title="See it in a real inbox first">
                <p>Sends a single test invitation to your own email or phone — using the exact template and formatting your guests will see.</p>
                <p><strong>Why do this before the real send</strong>:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Confirm the couple photo, RSVP button and dates render correctly</li>
                  <li>Check it doesn&apos;t land in Junk in Gmail / Outlook / iCloud</li>
                  <li>Verify the SMS looks right on your phone (line breaks, links)</li>
                </ul>
                <p>The RSVP link in the test points to a preview page (won&apos;t affect real guest data).</p>
              </InfoTip>
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {['email', 'sms', 'both'].map((c) => (
                <button key={c} data-testid={`test-channel-${c}`} onClick={() => setTestChannel(c)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${testChannel === c ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8]'}`}>{c === 'email' ? 'Email' : c === 'sms' ? 'Text' : 'Both'}</button>
              ))}
            </div>
            {testChannel !== 'sms' && (
              <input data-testid="test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Your email" className="mb-2 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            )}
            {testChannel !== 'email' && (
              <input data-testid="test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+14155551234" className="mb-2 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            )}
            <button data-testid="test-send-btn" disabled={testBusy} onClick={runTestSend} className="w-full btn-primary text-sm px-4 py-2 rounded-full inline-flex items-center justify-center gap-2">
              {testBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send test</>}
            </button>
            <p className="text-[10px] text-[#9A8A7E] mt-2">Recommended — check it looks right in a real inbox / on your phone before blasting your guests.</p>
          </section>

          <section data-testid="preview-email" className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
            <p className="section-eyebrow mb-2">Email preview</p>
            <p className="text-xs text-[#6A6A6A] mb-2 truncate"><strong>Subject:</strong> {renderPreview(tmpl.email.subject, previewCtx)}</p>
            <div className="rounded-lg border border-[#EFE3D8] overflow-hidden" dangerouslySetInnerHTML={{ __html: emailPreview }} />
          </section>

          <section data-testid="preview-sms" className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
            <p className="section-eyebrow mb-2">SMS preview</p>
            <div className="p-3 rounded-2xl bg-[#F4E4DA] text-sm text-[#2A2A2A] max-w-xs whitespace-pre-wrap">
              {smsPreview}
            </div>
          </section>
        </div>
      </div>

      {/* Send confirmation modal */}
      {confirming && (
        <div data-testid="send-confirm-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl p-6 border border-[#EFE3D8]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F4E4DA] flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#7B2C3B]" />
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#2A2A2A]">Send {selectedItems.length} invitation{selectedItems.length === 1 ? '' : 's'}?</h3>
                <p className="text-sm text-[#6A6A6A] mt-1">
                  {(!emailReady || !smsReady) && (
                    <span className="block mb-2 p-2 rounded-lg bg-[#FBF1EA] text-[#A0644C] text-xs">
                      Heads up: {!emailReady && 'email is currently simulated. '}{!smsReady && 'SMS is currently simulated. '}
                      Nothing will actually leave the server until you add the API keys.
                    </span>
                  )}
                  This will send the invitation to each selected guest using their chosen channel. Once sent, you can't recall it.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button data-testid="send-cancel" onClick={() => setConfirming(false)} className="text-sm px-4 py-2 rounded-full bg-white border border-[#EFE3D8] text-[#3A3A3A]">Cancel</button>
              <button data-testid="send-confirm" disabled={sendBusy} onClick={doSend} className="text-sm px-5 py-2 rounded-full bg-[#7B2C3B] text-white inline-flex items-center gap-2">
                {sendBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send now</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last result */}
      {lastResult && (
        <section data-testid="send-result" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
          <h3 className="font-serif text-xl text-[#2A2A2A] mb-2">Last dispatch</h3>
          <pre className="text-[11px] text-[#6A6A6A] whitespace-pre-wrap max-h-64 overflow-auto">{JSON.stringify(lastResult, null, 2)}</pre>
        </section>
      )}

      {showRecipientMgr && (
        <RecipientManagerModal
          onClose={() => setShowRecipientMgr(false)}
          guests={guests}
          filteredGuests={filteredGuests}
          selection={selection}
          selectedItems={selectedItems}
          selSummary={selSummary}
          toggleChannel={toggleChannel}
          applyBulkToFiltered={applyBulkToFiltered}
          clearAll={clearAll}
          selectAllEmail={selectAllEmail}
          selectAllSms={selectAllSms}
          selectAllBoth={selectAllBoth}
          selectNotInvited={selectNotInvited}
          statusOf={statusOf}
          statusTimeOf={statusTimeOf}
          STATUS_PILL={STATUS_PILL}
          filters={{ pkQuery, pkSide, pkGroup, pkHasEmail, pkHasPhone, pkStatus, pkRsvp }}
          setFilters={{ setPkQuery, setPkSide, setPkGroup, setPkHasEmail, setPkHasPhone, setPkStatus, setPkRsvp }}
          clearFilters={clearFilters}
          savedSegments={savedSegments}
          saveCurrentSegment={saveCurrentSegment}
          loadSegment={loadSegment}
          deleteSegment={deleteSegment}
          onOpenReview={() => { setShowRecipientMgr(false); setConfirming(true); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full-screen recipient manager — opens as a modal overlay so the couple can
// see every guest at once with search, filters, per-row status pills, and
// bulk actions. Selections persist back into the inline sidebar picker.
// ---------------------------------------------------------------------------
function RecipientManagerModal({
  onClose, guests, filteredGuests, selection, selectedItems, selSummary,
  toggleChannel, applyBulkToFiltered, clearAll,
  selectAllEmail, selectAllSms, selectAllBoth, selectNotInvited,
  statusOf, statusTimeOf, STATUS_PILL,
  filters, setFilters, clearFilters, savedSegments, saveCurrentSegment,
  loadSegment, deleteSegment, onOpenReview,
}) {
  const { pkQuery, pkSide, pkGroup, pkHasEmail, pkHasPhone, pkStatus, pkRsvp } = filters;
  const { setPkQuery, setPkSide, setPkGroup, setPkHasEmail, setPkHasPhone, setPkStatus, setPkRsvp } = setFilters;
  const anyFilter = !!(pkQuery || pkSide !== 'all' || pkGroup !== 'all' || pkHasEmail || pkHasPhone || pkStatus !== 'all' || pkRsvp !== 'all');
  const notInvitedCount = guests.filter((g) => (!g.email_status && !!g.email) || (!g.sms_status && !!g.phone)).length;
  const LEGEND = [
    { key: 'unsent',    dot: 'border border-[#D9CCBE] bg-transparent', label: 'Not sent yet' },
    { key: 'submitted', dot: 'bg-[#EFC9B0]',                            label: 'Queued' },
    { key: 'delivered', dot: 'bg-[#4A7A4A]',                            label: 'Delivered' },
    { key: 'opened',    dot: 'bg-[#7B2C3B]',                            label: 'Opened' },
    { key: 'bounced',   dot: 'bg-red-500',                              label: 'Bounced / spam' },
    { key: 'failed',    dot: 'bg-[#A0644C]',                            label: 'Failed' },
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-stretch p-0 sm:p-4" data-testid="recipient-mgr-modal">
      <div className="bg-white sm:rounded-2xl w-full max-w-6xl mx-auto flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-serif text-2xl text-[#2A2A2A] inline-flex items-center gap-2">
            <Users className="w-5 h-5" /> Recipient manager
            <span className="text-xs font-sans text-[#9A8A7E]">
              · {filteredGuests.length} of {guests.length} shown
            </span>
          </h3>
          <button data-testid="mgr-close" onClick={onClose} className="text-[#6A6A6A] hover:text-[#2A2A2A] inline-flex items-center gap-1 text-sm">
            <Minimize2 className="w-4 h-4" /> Close
          </button>
        </div>

        {/* Quick-select chips — same as the inline picker so muscle memory transfers */}
        <div className="px-4 py-3 border-b border-[#EFE3D8] bg-white flex items-center gap-2 flex-wrap">
          <button
            data-testid="mgr-select-not-invited"
            onClick={selectNotInvited}
            title="Select only guests we've never emailed or texted"
            className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#611E2E] inline-flex items-center gap-1"
          >
            <MailPlus className="w-3.5 h-3.5" /> Only not yet invited
            {notInvitedCount > 0 && (
              <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FBF7F2] text-[#7B2C3B]">{notInvitedCount}</span>
            )}
          </button>
          <button data-testid="mgr-select-all-email" onClick={selectAllEmail} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">Select all with email</button>
          <button data-testid="mgr-select-all-sms" onClick={selectAllSms} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">Select all with text</button>
          <button data-testid="mgr-select-all-both" onClick={selectAllBoth} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">Everyone</button>
          <button data-testid="mgr-clear-all" onClick={clearAll} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">Clear</button>

          {/* Color decoder for the Status column dots */}
          <span className="mx-2 hidden sm:inline-block h-5 w-px bg-[#EFE3D8]" aria-hidden="true" />
          <span className="text-[11px] font-medium text-[#7B2C3B]">Dot legend</span>
          {LEGEND.map((item) => (
            <span
              key={item.key}
              data-testid={`mgr-legend-${item.key}`}
              className="inline-flex items-center gap-1.5 text-[11px] text-[#6A6A6A]"
            >
              <span className={`w-2 h-2 rounded-full inline-block ${item.dot}`} />
              {item.label}
            </span>
          ))}
        </div>

        {/* Toolbar: search + filters + saved segments */}
        <div className="px-4 py-3 border-b border-[#EFE3D8] bg-[#FBF7F2] space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E]" />
              <input
                data-testid="mgr-search"
                value={pkQuery}
                onChange={(e) => setPkQuery(e.target.value)}
                placeholder="Search by name, email or phone"
                className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>
            <select data-testid="mgr-side" value={pkSide} onChange={(e) => setPkSide(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white">
              <option value="all">All sides</option>
              <option>Bride</option><option>Groom</option><option>Both</option>
            </select>
            <select data-testid="mgr-group" value={pkGroup} onChange={(e) => setPkGroup(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white">
              <option value="all">All groups</option>
              <option>Family</option><option>Friends</option><option>Work</option><option>Other</option>
            </select>
            <select data-testid="mgr-status" value={pkStatus} onChange={(e) => setPkStatus(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white">
              <option value="all">Any status</option>
              <option value="unsent">Not sent yet</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="bounced">Bounced / failed</option>
            </select>
            <select data-testid="mgr-rsvp" value={pkRsvp} onChange={(e) => setPkRsvp(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white" title="Hide guests who've already replied, or focus on only Yes / No">
              <option value="all">Any RSVP</option>
              <option value="pending">RSVP: Pending only</option>
              <option value="yes">RSVP: Yes only</option>
              <option value="no">RSVP: No only</option>
            </select>
            <button
              onClick={() => setPkHasEmail((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border ${pkHasEmail ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] bg-white'}`}
            >Has email</button>
            <button
              onClick={() => setPkHasPhone((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border ${pkHasPhone ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] bg-white'}`}
            >Has phone</button>
            {anyFilter && (
              <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded-full text-[#7B2C3B] hover:underline inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Clear filters
              </button>
            )}
          </div>

          {/* Saved segments */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-[#9A8A7E] inline-flex items-center gap-1"><Bookmark className="w-3 h-3" /> Segments:</span>
            {savedSegments.length === 0 && (
              <span className="text-[11px] text-[#9A8A7E] italic">No saved segments yet — set some filters then hit "Save current" to reuse them next send.</span>
            )}
            {savedSegments.map((seg) => (
              <span key={seg.id} className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#EFE3D8] inline-flex items-center gap-1">
                <button data-testid={`seg-load-${seg.id}`} onClick={() => loadSegment(seg)} className="text-[#7B2C3B] hover:underline">{seg.name}</button>
                <button data-testid={`seg-delete-${seg.id}`} onClick={() => deleteSegment(seg.id)} className="text-[#9A8A7E] hover:text-red-600" title="Delete this segment">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {anyFilter && (
              <button data-testid="seg-save" onClick={saveCurrentSegment} className="text-[11px] px-2 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
                <Save className="w-3 h-3" /> Save current
              </button>
            )}
          </div>
        </div>

        {/* Bulk actions bar */}
        <div className="px-4 py-2.5 bg-white border-b border-[#EFE3D8] flex items-center gap-2 flex-wrap text-xs">
          <span className="text-[#6A6A6A] inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> For the <strong>{filteredGuests.length}</strong> filtered:</span>
          <button data-testid="bulk-email" onClick={() => applyBulkToFiltered('email')} className="px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> Set to Email
          </button>
          <button data-testid="bulk-sms" onClick={() => applyBulkToFiltered('sms')} className="px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Set to Text
          </button>
          <button data-testid="bulk-both" onClick={() => applyBulkToFiltered('both')} className="px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white hover:bg-[#611E2E] inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Set to Both
          </button>
          <button data-testid="bulk-skip" onClick={() => applyBulkToFiltered('skip')} className="px-3 py-1.5 rounded-full bg-white text-[#6A6A6A] border border-[#EFE3D8] hover:bg-[#FBF7F2] inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Deselect
          </button>
          <button onClick={clearAll} className="px-3 py-1.5 rounded-full text-[#9A8A7E] hover:text-[#2A2A2A] hover:underline">
            Clear everything
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {filteredGuests.length === 0 ? (
            <div className="text-center py-16 text-[#6A6A6A]">
              {guests.length === 0
                ? <>No guests yet — head to the <strong>Guests</strong> tab first.</>
                : <>No guests match those filters. <button onClick={clearFilters} className="text-[#7B2C3B] underline">Clear filters</button>.</>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FBF7F2] text-[#6A6A6A] sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-normal">Name</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-normal">Contact</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-normal">Group / side</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-normal">Status</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider font-normal">Channel</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.map((g) => {
                  const pick = selection[g.id] || 'skip';
                  const s = statusOf(g);
                  const pill = STATUS_PILL[s] || STATUS_PILL.unsent;
                  const evtTime = statusTimeOf(g, s);
                  const evtStr = fmtDeliveryTime(evtTime);
                  const detail = buildDeliveryTooltip(g);
                  const tooltip = detail ? `${pill.label}\n\n${detail}` : pill.label;
                  return (
                    <tr key={g.id} data-testid={`mgr-row-${g.id}`} className="border-t border-[#EFE3D8] hover:bg-[#FBF7F2]/60">
                      <td className="px-4 py-2 text-[#2A2A2A]">
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span
                            data-testid={`mgr-name-dot-${g.id}`}
                            title={tooltip}
                            className={`w-2.5 h-2.5 rounded-full inline-block flex-shrink-0 ${pill.dot}`}
                          />
                          <span>{g.name || 'Unnamed'}</span>
                          {(() => {
                            // RSVP badge — so couples don't re-invite someone who's already replied.
                            const r = g.rsvp;
                            if (r === 'Yes') return <span data-testid={`mgr-rsvp-${g.id}`} className="text-[9px] uppercase tracking-wider bg-[#E6F1E6] text-[#2F5D2F] border border-[#B6D7B6] px-1.5 py-0.5 rounded-full">RSVP · Yes</span>;
                            if (r === 'No')  return <span data-testid={`mgr-rsvp-${g.id}`} className="text-[9px] uppercase tracking-wider bg-stone-100 text-stone-600 border border-stone-200 px-1.5 py-0.5 rounded-full">RSVP · No</span>;
                            return <span data-testid={`mgr-rsvp-${g.id}`} className="text-[9px] uppercase tracking-wider bg-[#FFF7EC] text-[#7B4A1A] border border-[#F5D9B7] px-1.5 py-0.5 rounded-full">RSVP · Pending</span>;
                          })()}
                          {!g.email && g.phone && (
                            <span className="text-[9px] uppercase tracking-wider bg-[#F4E4DA] text-[#7B2C3B] border border-[#B8896C] px-1.5 py-0.5 rounded-full">Needs email</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[11px] text-[#6A6A6A]">
                        <div className="truncate max-w-[240px]">{g.email || <span className="text-[#B8896C]">no email</span>}</div>
                        <div className="truncate max-w-[240px]">{g.phone || <span className="text-[#B8896C]">no phone</span>}</div>
                      </td>
                      <td className="px-4 py-2 text-[11px] text-[#6A6A6A]">
                        <div>{g.group || '—'}</div>
                        <div className="text-[10px] text-[#9A8A7E]">{g.side || '—'}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          title={tooltip}
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 ${pill.color}`}
                        >
                          <span className={`w-2 h-2 rounded-full inline-block ring-1 ring-white/60 ${pill.dot}`} />
                          {pill.label}
                        </span>
                        {evtStr && (
                          <div data-testid={`mgr-status-time-${g.id}`} className="text-[10px] text-[#9A8A7E] mt-1">{evtStr}</div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            data-testid={`mgr-pick-email-${g.id}`}
                            disabled={!g.email}
                            onClick={() => toggleChannel(g.id, 'email')}
                            className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${
                              (pick === 'email' || pick === 'both') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8]'
                            } ${!g.email ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            <Mail className="w-3 h-3" /> Email
                          </button>
                          <button
                            data-testid={`mgr-pick-sms-${g.id}`}
                            disabled={!g.phone}
                            onClick={() => toggleChannel(g.id, 'sms')}
                            className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${
                              (pick === 'sms' || pick === 'both') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8]'
                            } ${!g.phone ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            <MessageSquare className="w-3 h-3" /> Text
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[#EFE3D8] bg-[#FBF7F2] flex items-center justify-between gap-3 flex-wrap">
          <p data-testid="mgr-summary" className="text-xs text-[#6A6A6A]">
            <strong className="text-[#2A2A2A]">{selectedItems.length}</strong> selected
            {selSummary.total > 0 && (
              <span className="ml-1">
                · <span className="text-[#2A2A2A]">{selSummary.email}</span> email
                · <span className="text-[#2A2A2A]">{selSummary.sms}</span> text
                · <span className="text-[#2A2A2A]">{selSummary.both}</span> both
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] text-[#3A3A3A] hover:bg-white">
              Done
            </button>
            <button
              data-testid="mgr-open-review"
              disabled={selectedItems.length === 0}
              onClick={onOpenReview}
              className={`btn-primary text-sm px-5 py-2 rounded-full inline-flex items-center gap-2 ${selectedItems.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Send className="w-4 h-4" /> Review &amp; send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiField({ testId, label, placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">{label}</label>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
      />
    </div>
  );
}
